import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, Lock, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { customerAuthService } from '../services/customerAuthService';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { toast } from 'sonner';
import { RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider, signInWithCredential } from 'firebase/auth';
import { auth } from '../config/firebase';

const CustomerLogin = () => {
  const navigate = useNavigate();
  const { loginWithFirebase } = useCustomerAuth();
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null); // persistent error display
  const recaptchaVerifierRef = useRef(null);

  useEffect(() => {
    auth.languageCode = 'en';
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
      }
    };
  }, [auth]);

  const setupRecaptcha = () => {
    if (!recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA verified');
        },
        'expired-callback': () => {
          toast.error('reCAPTCHA expired. Please try again.');
        }
      });
    }
    return recaptchaVerifierRef.current;
  };

  const validatePhone = (phoneNumber) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    
    if (!validatePhone(phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      const recaptchaVerifier = setupRecaptcha();
      await recaptchaVerifier.render();
      
      const phoneNumber = `+91${phone}`;
      const result = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      
      setConfirmationResult(result);
      toast.success('OTP sent successfully!');
      setStep('otp');
    } catch (error) {
      console.error('Send OTP error:', error);
      let errorMessage = 'Failed to send OTP';
      
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'SMS quota exceeded. Please contact support.';
      } else if (error.code === 'auth/captcha-check-failed') {
        errorMessage = 'reCAPTCHA verification failed. Please try again.';
      }
      
      toast.error(errorMessage);
      
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    if (!confirmationResult) {
      toast.error('Please request OTP first');
      setStep('phone');
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const credential = PhoneAuthProvider.credential(confirmationResult.verificationId, otp);
      const userCredential = await signInWithCredential(auth, credential);
      const idToken = await userCredential.user.getIdToken();

      // Log the token for debugging (remove in production)
      console.log('[DEBUG] Firebase ID Token:', idToken);

      let response;
      try {
        response = await loginWithFirebase(idToken, phone);
      } catch (backendErr) {
        // Axios throws on non-2xx — capture the backend message directly
        const msg =
          backendErr?.response?.data?.message ||
          backendErr?.message ||
          'Backend authentication failed';
        console.error('[DEBUG] Backend error:', backendErr?.response?.data);
        setErrorMessage(`Backend error: ${msg}`);
        toast.error(msg);
        return;
      }

      if (response?.success) {
        toast.success('Login successful!');
        navigate('/customer/dashboard');
      } else {
        const msg = response?.message || 'Authentication failed';
        setErrorMessage(`Auth failed: ${msg}`);
        toast.error(msg);
      }
    } catch (error) {
      console.error('[DEBUG] Verify OTP error:', error);
      let msg = 'OTP verification failed';

      if (error.code === 'auth/invalid-verification-code') {
        msg = 'Invalid OTP. Please try again.';
      } else if (error.code === 'auth/code-expired') {
        msg = 'OTP has expired. Please request a new one.';
        setStep('phone');
        setOtp('');
        setConfirmationResult(null);
      } else if (error.code === 'auth/invalid-credential') {
        msg = 'Invalid verification credentials.';
      }

      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhone(value);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setOtp('');
    setConfirmationResult(null);
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
  };

  const handleResendOtp = async () => {
    setStep('phone');
    setOtp('');
    setConfirmationResult(null);
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
    toast.info('Please click "Send OTP" to receive a new code');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        <Card className="p-8 bg-white shadow-xl border-gray-100">
          <div className="text-center mb-8">
            <div className="inline-block p-3 bg-gradient-to-br from-teal-600 to-blue-500 rounded-2xl mb-4 shadow-lg">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Customer Login</h1>
            <p className="text-gray-600">
              {step === 'phone' ? 'Enter your phone number' : 'Verify your OTP'}
            </p>
          </div>

          {step === 'phone' ? (
            <form onSubmit={handleSendOtp} className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="Enter 10-digit mobile number"
                    required
                    className="pl-10 h-12 border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  We'll send you a 6-digit verification code via SMS
                </p>
              </div>

              <div id="recaptcha-container"></div>

              <Button
                type="submit"
                disabled={loading || phone.length !== 10}
                className="w-full bg-gradient-to-r from-teal-600 to-blue-500 hover:from-teal-700 hover:to-blue-600 text-white h-12 text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              {/* Persistent error display — stays visible even after re-renders */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-300 text-red-800 rounded-lg px-4 py-3 text-sm break-words">
                  <strong>Error:</strong> {errorMessage}
                </div>
              )}
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="otp"
                    type="text"
                    name="otp"
                    value={otp}
                    onChange={handleOtpChange}
                    placeholder="Enter 6-digit OTP"
                    required
                    className="pl-10 h-12 border-gray-200 text-center tracking-widest text-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                    maxLength={6}
                    autoFocus
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  OTP sent to +91 {phone}
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-gradient-to-r from-teal-600 to-blue-500 hover:from-teal-700 hover:to-blue-600 text-white h-12 text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify OTP'
                )}
              </Button>

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  className="w-full flex items-center justify-center gap-2 text-sm text-teal-600 hover:text-teal-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded-md py-2"
                >
                  Didn't receive OTP? Resend
                </button>
                <button
                  type="button"
                  onClick={handleBack}
                  className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-teal-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded-md py-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Change phone number
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
              <ShieldCheck className="h-4 w-4 text-green-600" />
              <span>Secured with Firebase Authentication</span>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Want to teach?{' '}
              <Link to="/tutor/register" className="text-teal-600 hover:text-teal-700 font-medium transition-colors duration-200">
                Register as Tutor
              </Link>
            </p>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-teal-600 transition-colors duration-200">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
